import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import axios from "axios";
import Cookie from "js-cookie";
import Modal from "react-modal";

import { useStore } from "../../helpers/store_context";
import handleAxiosError from "../../helpers/handle_axios_error";
import watchDialogFocus from "../../helpers/watch_dialog_focus";
import toastStore from "../../stores/toast_store";
import { communityNow } from "../../helpers/helpers";
import { RESET_PASSWORD_MODAL } from "../../routes";
import ResidentsPasswordNew from "./password_new";

// Where to send a signed-in resident when no redirect target was saved:
// today's calendar, in the community's timezone.
function defaultFrom() {
  return { pathname: `/calendar/all/${communityNow().format("YYYY-MM-DD")}` };
}

const styles = {
  box: {
    marginRight: "auto",
    marginLeft: "auto",
    paddingRight: "15px",
    paddingLeft: "15px",
    width: "100%",
  },
};

Modal.setAppElement("#root");

const ResidentsLogin = observer(() => {
  const store = useStore();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // The requests outlive a navigation away; the flag keeps their
  // callbacks from setting state after unmount, like the class's
  // _isMounted.
  const mountedRef = useRef(true);
  // True from a successful login until the full page load replaces this page.
  const reloadingRef = useRef(false);
  useEffect(function () {
    mountedRef.current = true;
    return function () {
      mountedRef.current = false;
    };
  }, []);

  // The id of the "Email required." message this page showed, or null.
  // It comes from a check the page makes itself, so it goes as soon as
  // the email box holds an email, without waiting for the person to
  // close it (#137).
  const emailRequiredRef = useRef(null);
  // The ids of the errors that tries of "Reset your password" got. A try
  // that works shows that their cause is fixed, so it takes them away.
  const resetErrorsRef = useRef([]);

  useEffect(
    function () {
      if (emailRequiredRef.current === null || email.trim() === "") return;
      toastStore.remove(emailRequiredRef.current);
      emailRequiredRef.current = null;
    },
    [email],
  );

  // The email with the spaces around it taken off, or null after saying
  // that an email is required. The server checks the same thing, with
  // the same words; asking first saves a trip to the server, and makes
  // the message one the page can take away.
  function emailOrSayRequired() {
    const trimmedEmail = email.trim();
    if (trimmedEmail !== "") return trimmedEmail;
    emailRequiredRef.current = toastStore.show("Email required.", "error");
    return null;
  }

  // The reset link opens /reset-password#token=... . A link mailed
  // before 2026-10-23 opens /reset-password/<token>, and the form then
  // says that link no longer works.
  const modalOpen = params.modal === RESET_PASSWORD_MODAL;

  function handleCloseModal() {
    navigate("/");
  }

  function handleResetPassword() {
    const trimmedEmail = emailOrSayRequired();
    if (trimmedEmail === null) return;

    setLoading(true);
    axios
      .post(`/api/v1/residents/password-reset`, { email: trimmedEmail })
      .then(function (response) {
        if (!mountedRef.current) return;
        setLoading(false);
        resetErrorsRef.current.forEach(function (id) {
          toastStore.remove(id);
        });
        resetErrorsRef.current = [];
        toastStore.show(response.data.message, "success");
      })
      .catch(function (error) {
        if (!mountedRef.current) return;
        setLoading(false);
        resetErrorsRef.current.push(handleAxiosError(error));
      });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (emailOrSayRequired() === null) return;
    setLoading(true);

    axios
      .post(`/api/v1/residents/token`, {
        email: email,
        password: password,
      })
      .then(function (response) {
        if (!mountedRef.current) return;

        // The page is about to be replaced by a full load (below), so
        // the loader stays up and the Navigate branch in render is
        // switched off first. Without both, the re-render finds the
        // token cookie, mounts the calendar client-side, and its first
        // requests are cancelled by the reload a few ms later — WebKit
        // reports that as an uncaught error (#80).
        reloadingRef.current = true;
        Cookie.set("token", response.data.token, {
          expires: 7300,
        });
        Cookie.set("community_id", response.data.community_id, {
          expires: 7300,
        });
        Cookie.set("resident_id", response.data.resident_id, {
          expires: 7300,
        });
        Cookie.set("username", response.data.username, {
          expires: 7300,
        });
        if (response.data.timezone) {
          Cookie.set("timezone", response.data.timezone, {
            expires: 7300,
          });
        }

        // The timezone cookie was just written (if present in the
        // response), so defaultFrom() picks up the fresh value here.
        // `from` is where PrivateRoute sent the visitor from, or today's
        // calendar.
        var { from } = location.state || { from: defaultFrom() };
        window.location.href = from.pathname;
      })
      .catch(function (error) {
        if (!mountedRef.current) return;
        setLoading(false);
        handleAxiosError(error);
      });
  }

  const { from } = location.state || { from: defaultFrom() };

  if (
    !reloadingRef.current &&
    typeof Cookie.get("token") !== "undefined" &&
    Cookie.get("token") !== "undefined"
  ) {
    return <Navigate to={from} replace />;
  }

  return (
    <div>
      <header className="flex space-between header">
        <h2 className="pad-l-sm">Comeals</h2>
        {store.isOnline ? (
          <span className="online">ONLINE</span>
        ) : (
          <span className="offline">OFFLINE</span>
        )}
      </header>
      <div style={styles.box}>
        <br />
        <div>
          <form onSubmit={handleSubmit}>
            <fieldset className="login-box">
              <legend>Resident Login</legend>
              <label className="w-80" htmlFor="login-email">
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  placeholder="Email"
                  autoCapitalize="none"
                  autoComplete="username"
                  disabled={loading}
                  aria-label="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <br />
              <label className="w-80" htmlFor="login-password">
                <input
                  id="login-password"
                  name="password"
                  type="password"
                  placeholder="Password"
                  autoComplete="current-password"
                  disabled={loading}
                  aria-label="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
            </fieldset>

            <button
              className={loading ? "button-loader" : ""}
              type="submit"
              disabled={loading}
            >
              Submit
            </button>
          </form>
          <br />
          <button
            type="button"
            className="text-black button-link"
            disabled={loading}
            onClick={handleResetPassword}
          >
            Reset your password
          </button>
        </div>
      </div>
      <Modal
        isOpen={modalOpen}
        contentLabel="Login Modal"
        onRequestClose={handleCloseModal}
        // The form disables its field and Submit while its request is
        // out, which in Chrome moves focus to <body>, where Escape
        // does nothing (#152).
        contentRef={watchDialogFocus}
        style={{
          content: {
            backgroundColor: "var(--powder-blue)",
          },
        }}
      >
        {modalOpen && <ResidentsPasswordNew />}
      </Modal>
    </div>
  );
});

export default ResidentsLogin;
